import type { ColumnDef, ExpandedState, Row } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { EmptyTableMessage } from "@/core/components/empty-table-message";
import { SimpleTable } from "@/core/components/simple-table";
import { createExpandingColumn } from "@/core/components/table-columns/expanding-column";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { useBusiness } from "@/core/business";
import { sortDateDescending } from "@/core/utils/date";
import type {
  EnrichedSpiffApproval,
  EnrichedSpiffApprovalUser,
} from "@/spiff-approvals";
import {
  ClosedReferralCol,
  NewReferralCol,
  SignupsCol,
  TechnicianWithPaymentStatusCol,
  PaymentAmountCol,
  PaymentDateCol,
  ReviewsCol,
  YardSignsCol,
  PricebookSalesCol,
  DrivingScoresCol,
  TechGeneratedLeadsCol,
} from "@/spiffs/common/spiff-payments-table";
import { getCompoundId } from "./utils";

interface ApprovalsHistoryTableProps {
  filteredApprovals: EnrichedSpiffApproval[];
  isLoading: boolean;
  hasApprovals: boolean;
  onRowClick: (spiffApprovalId: string, userId: string) => void;
}

export const ApprovalsHistoryTable = ({
  filteredApprovals,
  hasApprovals,
  isLoading,
  onRowClick,
}: ApprovalsHistoryTableProps) => {
  const { isSpiffOnly } = useBusiness();
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const columns: ColumnDef<EnrichedSpiffApproval>[] = useMemo(
    () =>
      [
        createExpandingColumn<EnrichedSpiffApproval>(),
        TechnicianWithPaymentStatusCol<EnrichedSpiffApproval>(),
        PaymentDateCol<EnrichedSpiffApproval>(),
        !isSpiffOnly && SignupsCol<EnrichedSpiffApproval>(),
        !isSpiffOnly && NewReferralCol<EnrichedSpiffApproval>(),
        !isSpiffOnly && ClosedReferralCol<EnrichedSpiffApproval>(),
        ReviewsCol<EnrichedSpiffApproval>(),
        YardSignsCol<EnrichedSpiffApproval>(),
        PricebookSalesCol<EnrichedSpiffApproval>(),
        TechGeneratedLeadsCol<EnrichedSpiffApproval>(),
        DrivingScoresCol<EnrichedSpiffApproval>(),
        PaymentAmountCol<EnrichedSpiffApproval>(),
      ].filter(Boolean) as ColumnDef<EnrichedSpiffApproval>[],
    [isSpiffOnly],
  );

  // Sort approvals by display date descending (most recent first)
  const sortedGroupedApprovals = useMemo(
    () =>
      [...filteredApprovals].sort((a, b) =>
        sortDateDescending(a.display_date, b.display_date),
      ),
    [filteredApprovals],
  );

  const handleRowClick = (
    row: EnrichedSpiffApproval | EnrichedSpiffApprovalUser,
    tableRow?: Row<EnrichedSpiffApproval | EnrichedSpiffApprovalUser>,
  ) => {
    // If this is a grouped approval row with multiple users (parent row), toggle expansion
    if ("children" in row && row.children && row.children.length > 0) {
      if (tableRow) {
        tableRow.toggleExpanded();
      }
      return;
    }

    // For single-user rows or child rows, open the sidebar
    const userRow = row as EnrichedSpiffApprovalUser;
    onRowClick(userRow.spiff_approval_id, userRow.user_id);
  };

  if (sortedGroupedApprovals.length === 0 && !isLoading) {
    if (hasApprovals) {
      return (
        <Stack className="p-8">
          <EmptyTableMessage>
            No spiff approvals found in this time range.
          </EmptyTableMessage>
        </Stack>
      );
    }

    return (
      <Stack className="p-8">
        <EmptyTableMessage>
          <Typography variant="bodyW1" textColor="secondary">
            No spiff approvals yet. Go to the{" "}
            <Link
              to={`/spiffs/pending-spiffs`}
              className="cursor-pointer text-primary-text underline"
            >
              Payments due
            </Link>{" "}
            tab to see pending spiffs to approve.
          </Typography>
        </EmptyTableMessage>
      </Stack>
    );
  }

  return (
    <SimpleTable
      columns={columns}
      increaseHeaderPadding
      data={sortedGroupedApprovals}
      enableRowExpanding
      expanded={expanded}
      onExpandedChange={setExpanded}
      getSubRows={(row) => row.children}
      getRowId={(row) => {
        // For single-user rows and child rows (EnrichedSpiffApprovalUser), use compound ID
        if (
          "user_id" in row &&
          typeof row.user_id === "string" &&
          "spiff_approval_id" in row &&
          typeof row.spiff_approval_id === "string"
        ) {
          return getCompoundId(row.spiff_approval_id, row.user_id);
        }
        // For parent rows (EnrichedSpiffApproval), use the id field
        return row.id;
      }}
      onRowClick={handleRowClick}
      scrollAreaClassName="pr-0"
      isLoading={isLoading}
      tableClassName="min-w-[600px]"
      columnPinning={{
        left: ["expand", "technician-with-payment-status", "approval-date"],
        right: ["payment-amount"],
      }}
      enableStripedRows
    />
  );
};
